import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup';
import {
    ID_ACTIVE,
    ID_ANCILLARIES_EXTRA,
    ID_CORPORATE_EXTRA,
    ID_INACTIVE,
    ID_RETAIL_EXTRA,
    ID_STANDARD
} from '../constants/ClusterStatuses';
import {
    clusterDefaultValues,
    ID_FILTER_MSTYPE,
    ID_FILTER_NAME,
    ID_FILTER_SEARCH,
    ID_FILTER_STATUS,
    ID_FILTER_VALID_FROM,
    ID_FILTER_VALID_TO,
    ID_FILTER_LEGAL_ENTITIES,
    ID_FILTER_DEALERS,
    ID_FILTER_CLUSTERS_TYPE
} from "../constants/ClustersFilters";

import { addFilter, getFilterValue, renderMultiSelect } from "../../../utils/FilterUtils";
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { STATE_CLUSTERS_ACTIVE_FILTERS } from '../constants/ClustersRecoil';
import { ID_LICENSEES, ID_MANDATORY } from "../constants/ClusterMSTypes";
import { useClustersRepository } from "../../../api/repositories/ClustersRepository";
import { COLOR_ACTIVE } from '../constants/ClustersColors';
import AdvancedFilter from "../../../component/AdvancedFilter";
import { MenuItem, TextField } from "@mui/material";
import AsyncAutocomplete from "../../../component/AsyncAutocomplete";
import { DatePicker } from "@mui/x-date-pickers";
import { asEndOfDay, asStartOfDay } from "../../../utils/FinanceDateUtils";
import { GlobalContext } from '../../../../../util/state/GlobalContextProvider';

function ClustersFilter() {
    const { t } = useTranslation('finance', { keyPrefix: 'clusters' });
    const { t: tg } = useTranslation("translation");
    const setFilterDefs = useSetRecoilState(STATE_CLUSTERS_ACTIVE_FILTERS);
    const { getClusterNames } = useClustersRepository();
    const [search, setSearch] = useState("");
    const filterDefs = useRecoilValue(STATE_CLUSTERS_ACTIVE_FILTERS);
    const {
        globalData: { user },
    } = React.useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
    }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const [dealers, setDealers] = useState<any[]>(dealerOptions);

    const schema = Yup.object().shape({
        storeTypes: Yup.string(),
        clustersTypes: Yup.string(),
        statuses: Yup.string(),
        validFrom: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.validTo || (value <= context.parent.validTo);
        }).typeError(t('filters.errors.invalid-date')),
        validTo: Yup.date().nullable().test("testTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.validFrom || (value >= context.parent.validFrom);
        }).typeError(t('filters.errors.invalid-date')),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne'))
    });

    const defaultValues = {
        storeTypes: getFilterValue(ID_FILTER_MSTYPE, filterDefs) || "",
        clustersTypes: getFilterValue(ID_FILTER_CLUSTERS_TYPE, filterDefs) || "",
        name: getFilterValue(ID_FILTER_NAME, filterDefs) || null,
        statuses: getFilterValue(ID_FILTER_STATUS, filterDefs) || "",
        validFrom: getFilterValue(ID_FILTER_VALID_FROM, filterDefs) || null,
        validTo: getFilterValue(ID_FILTER_VALID_TO, filterDefs) || null,
        legalEntities: getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) || legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || [],
    };


    const renderStoreTypesField = (field: any, error: any, state: any, values: any) => (
        <TextField fullWidth
            select
            label={t("filters.clusters." + ID_FILTER_MSTYPE)}
            value={field.value}
            onChange={field.onChange}
        >
            <MenuItem value={""}>
                <em />
            </MenuItem>
            {storeTypes.map((option: any) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    )

    const renderClusterTypesField = (field: any, error: any, state: any, values: any) => (
        <TextField fullWidth
            select
            label={t("filters.clusters." + ID_FILTER_CLUSTERS_TYPE)}
            value={field.value}
            onChange={field.onChange}
        >
            <MenuItem value={""}>
                <em />
            </MenuItem>
            {clusterTypes.map((option: any) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    )

    const renderNameField = (field: any, error: any, state: any, values: any) => (
        <AsyncAutocomplete
            id={'name'}
            callback={(it) => getClusterNames(it).then(it => it.result)}
            value={field.value}
            onChange={(selected) => {
                field.onChange(selected);
            }}
            initialValues={true}
            renderInput={(params) => <TextField
                {...params}
                label={t('filters.clusters.' + ID_FILTER_NAME)}
            />}
        />
    )

    const renderStatusesField = (field: any, error: any, state: any, values: any) => (
        <TextField fullWidth
            select
            label={t("filters.clusters." + ID_FILTER_STATUS)}
            value={field.value}
            onChange={field.onChange}
        >
            <MenuItem value={""}>
                <em />
            </MenuItem>
            {statuses.map((option: any) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    )

    const renderValidFromField = (field: any, error: any, state: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.clusters.' + ID_FILTER_VALID_FROM)}
            value={field.value}
            maxDate={values['validTo'] !== null ? values['validTo'] : null}
            onChange={field.onChange}
            renderInput={(params) => <TextField {...params} style={{ width: "100%" }}
                error={error !== undefined}
                helperText={error ? error.message : ""}
                id={"validFrom"}
            />}
            inputFormat={"L"}
        />
    )

    const renderValidToField = (field: any, error: any, state: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filters.clusters.' + ID_FILTER_VALID_TO)}
            value={field.value}
            minDate={values['validFrom'] !== null ? values['validFrom'] : null}
            onChange={field.onChange}
            renderInput={(params) => <TextField {...params} style={{ width: "100%" }}
                error={error !== undefined}
                helperText={error ? error.message : ""}
                id={"validTo"}
            />}
            inputFormat={"L"}
        />
    )

    const fields = {
        storeTypes: renderStoreTypesField,
        clustersTypes: renderClusterTypesField,
        name: renderNameField,
        validFrom: renderValidFromField,
        validTo: renderValidToField,
        statuses: renderStatusesField,
        legalEntities: renderMultiSelect(
            tg("shared.legalEntities"),
            legalEntityOptions,
            "legalEntities",
            legalEntityOptions.length <= 1,
            (value: any, setValue: any) => {
            setValue('dealers', []);
            let dealers = user?.dealers
                ?.filter(
                (x: any) =>
                    value?.length > 0 &&
                    x.legalEntities.some((e: any) => value.includes(e.name)) &&
                    x.name.toLowerCase() !== "all"
                )
                .map((x: any) => {
                return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                };
                });
            setDealers(dealers);
            }
        ),
        dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),
    };

    const storeTypes = [
        {
            value: ID_MANDATORY,
            label: t('ms-types.' + ID_MANDATORY)
        },
        {
            value: ID_LICENSEES,
            label: t('ms-types.' + ID_LICENSEES)
        }
    ];

    const clusterTypes = [
        {
            value: ID_STANDARD,
            label: t('cluster-types.' + ID_STANDARD)
        },
        {
            value: ID_RETAIL_EXTRA,
            label: t('cluster-types.' + ID_RETAIL_EXTRA)
        },
        {
            value: ID_CORPORATE_EXTRA,
            label: t('cluster-types.' + ID_CORPORATE_EXTRA)
        },
        {
            value: ID_ANCILLARIES_EXTRA,
            label: t('cluster-types.' + ID_ANCILLARIES_EXTRA)
        }
    ];

    const statuses = [
        {
            value: ID_ACTIVE,
            label: t('filters.clusters.statuses.' + ID_ACTIVE),
            color: COLOR_ACTIVE + '.transparent'
        },
        {
            value: ID_INACTIVE,
            label: t('filters.clusters.statuses.' + ID_INACTIVE),
            color: 'neutral.light'
        }
    ];

    const submit = (data: any) => {

        addFilter(setFilterDefs, ID_FILTER_SEARCH, search);
        addFilter(setFilterDefs, ID_FILTER_NAME, data.name);
        addFilter(setFilterDefs, ID_FILTER_VALID_FROM, asStartOfDay(data.validFrom));
        addFilter(setFilterDefs, ID_FILTER_VALID_TO, asEndOfDay(data.validTo));
        addFilter(setFilterDefs, ID_FILTER_STATUS, data.statuses);
        addFilter(setFilterDefs, ID_FILTER_MSTYPE, data.storeTypes);
        addFilter(setFilterDefs, ID_FILTER_CLUSTERS_TYPE, data.clustersTypes);
        addFilter(setFilterDefs, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilterDefs, ID_FILTER_DEALERS, data.dealers);
        
    }

    const reset = () => {
        setFilterDefs([]);
    }

    return (
        <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
            searchInput={search} setSearchInput={setSearch}
            onSubmit={submit}
            onReset={reset}
            customDefaultValues={clusterDefaultValues}

        />
    )
}

export default ClustersFilter;